import { makeAPIURL } from "./../env";
import {
  InternalServerError,
  IRPCErrorOptions,
  NetworkError,
  RPCError,
  UnknownRPCError,
} from "../errors/taxonomy";
import { SHARED_ERRORS } from "~src/shared/apigen/errors";

interface ErrorClass {
  new (message: string, options: IRPCErrorOptions): RPCError;
}

type IErrors = { code: string; ErrorClass: ErrorClass }[];

type IParams<ReqData> = {
  router: string;
  routePath: string;
  args: ReqData;
  errors: IErrors;
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
};

export const fetchRPCRoute = async <ReqData, RespData>({
  router,
  routePath,
  args,
  errors,
  method,
}: IParams<ReqData>): Promise<RespData> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  let response;
  let text;
  try {
    response = await fetch(makeAPIURL() + `/${router}/${routePath}`, {
      method,
      cache: "no-store",
      credentials: "include",
      headers,
      body: args === undefined ? undefined : JSON.stringify(args),
    });
    text = await response.text();
  } catch (e) {
    throw new NetworkError(`Network error: ${e}`, { router, routePath });
  }

  if (response.status === 404) {
    const error = new UnknownRPCError(`Unknown route ${router}/${routePath}.`, {
      status: response.status,
      data: {},
      router,
      routePath,
    });
    throw error;
  }

  let data;
  try {
    data = JSON.parse(text).data;
  } catch (e) {
    const error = new UnknownRPCError("Failed to decode JSON.", {
      status: response.status,
      data: {},
      router,
      routePath,
    });
    throw error;
  }

  try {
    data = deserialize(data);
  } catch (e) {
    const error = new CurrencyDeserializationError(
      "Failed to deserialize currency data.",
      {
        status: response.status,
        data: {},
        router,
        routePath,
      },
    );
    throw error;
  }

  const statusType = Math.floor(response.status / 100);
  if (statusType === 5) {
    throw new InternalServerError(text, { router, routePath });
  }

  if (statusType !== 2) {
    const errorData = data as { code?: string };

    for (const { code, ErrorClass } of SHARED_ERRORS) {
      if (errorData.code === code) {
        throw new ErrorClass(errorData.code, {
          router,
          routePath,
          status: response.status,
        });
      }
    }

    for (const { code, ErrorClass } of errors) {
      if (errorData.code === code) {
        throw new ErrorClass(errorData.code, {
          router,
          routePath,
          status: response.status,
          data: errorData,
        });
      }
    }

    const error = new UnknownRPCError("An unknown RPC error occurred.", {
      status: response.status,
      data: errorData,
      router,
      routePath,
    });
    throw error;
  }

  return data as RespData;
};

type ICurrencySentinel =
  | {
      __sentinel: "Money" | undefined;
      amount: number;
      code: string;
    }
  | unknown;

type IInput = { [key: string]: ICurrencySentinel | IInput | any };

export const deserialize = (obj: IInput): IInput => {
  if (Array.isArray(obj)) {
    return obj.map(deserialize);
  }
  if (
    obj !== null &&
    typeof obj === "object" &&
    Boolean(Object.getPrototypeOf(obj).isPrototypeOf(Object))
  ) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, deserialize(v)]),
    );
  }

  return obj;
};

class CurrencyDeserializationError extends RPCError {
  static code = "SHARED/BASE/CURRENCY_DESERIALIZATION_ERROR";
}
